/* femme Appstle migration core V4.0 | 2026-11-02
   Appstle-first bridge with flexible per-product selling-plan groups.
   - Uses the Appstle group actually available for the product instead of one
     permanently hard-coded group.
   - If multiple Appstle groups are present, resolves the group exposed by
     Appstle's native widget; ambiguous groups are not merged together.
   - Keeps the original Multi Choice Plan as a backwards-compatible known plan,
     but no longer requires it.
   - Reads Shopify selling-plan price adjustments so customer-facing subscription
     copy can reflect flat or staged discounts without hard-coded 15%/20% terms.
*/
(() => {
  'use strict';

  const A = window.FemmeAppstleMigration = window.FemmeAppstleMigration || {};

  A.APP_ID = 'appstle';
  A.LEGACY_GROUP_ID = '100555522435';
  A.LEGACY_GROUP_NAME = 'Multi Choice Plan';
  A.DEFAULT_PLAN_ID = '713609675139';
  A.PLAN_LABELS = {
    '713609642371': 'Every 2 weeks',
    '713609675139': 'Every month',
    '713609707907': 'Every 2 months',
    '713609740675': 'Every 3 months'
  };

  A.APPSTLE_SELECTOR = [
    '[id^="appstle_subscription_widget"]',
    '[id*="appstle_subscription_widget"]',
    '.appstle_subscription_wrapper',
    '.appstle-subscription-widget',
    '[data-appstle-subscription-widget]',
    '[class*="appstle_subscription"]',
    '[class*="appstle-subscription"]'
  ].join(',');

  A.cache = A.cache || new Map();
  A.state = A.state || {
    controller: null,
    root: null,
    config: null,
    baseConfig: null,
    product: null,
    variants: new Map(),
    ui: null,
    selectedPlanId: '',
    selectionType: 'subscription',
    boundForms: new Set(),
    observer: null,
    raf: null
  };

  A.isAppstleControl = (control) =>
    control instanceof Element && Boolean(control.closest(A.APPSTLE_SELECTOR));

  A.isAppstleSellingPlanRadio = (control) => {
    if (!(control instanceof HTMLInputElement)) return false;
    if (String(control.type || '').toLowerCase() !== 'radio') return false;
    if (control.name !== 'selling_plan') return false;
    return A.isAppstleControl(control);
  };

  A.currentPlanIds = () => {
    const ids = new Set();
    A.state?.variants?.forEach?.((variant) => {
      (variant?.plans || []).forEach((plan) => {
        if (plan?.id) ids.add(A.normaliseId(plan.id));
      });
    });
    Object.keys(A.PLAN_LABELS).forEach((id) => ids.add(id));
    return ids;
  };

  A.extractKnownPlanId = (value) => {
    const raw = String(value || '');
    const normalised = A.normaliseId(raw);
    const knownIds = A.currentPlanIds();

    if (knownIds.has(normalised)) return normalised;
    return Array.from(knownIds).find((planId) => raw.includes(planId)) || '';
  };

  A.planForId = (planId) => {
    const wanted = A.normaliseId(planId);
    let match = null;
    A.state?.variants?.forEach?.((variant) => {
      if (match) return;
      match = (variant?.plans || []).find(
        (plan) => A.normaliseId(plan?.id) === wanted
      ) || null;
    });
    return match;
  };

  A.planTextMatches = (text, planId) => {
    const value = String(text || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    if (!value) return false;

    switch (A.normaliseId(planId)) {
      case '713609642371':
        return (
          /\bfortnight(?:ly)?\b/.test(value) ||
          /\bevery\s+2\s+weeks?\b/.test(value) ||
          /\b2[\s-]*weekly\b/.test(value) ||
          /^2\s+weeks?$/.test(value)
        );
      case '713609675139':
        return (
          /\bevery\s+(?:1\s+)?month\b/.test(value) ||
          /\b1\s+monthly\b/.test(value) ||
          /^1\s+month$/.test(value) ||
          /^monthly(?:\s+subscription)?$/.test(value)
        );
      case '713609707907':
        return (
          /\bevery\s+2\s+months?\b/.test(value) ||
          /\b2\s+monthly\b/.test(value) ||
          /^2\s+months?$/.test(value)
        );
      case '713609740675':
        return (
          /\bevery\s+3\s+months?\b/.test(value) ||
          /\b3\s+monthly\b/.test(value) ||
          /^3\s+months?$/.test(value)
        );
      default: {
        const plan = A.planForId(planId);
        if (!plan) return false;
        const planName = String(plan.name || '').trim().toLowerCase();
        const displayLabel = String(A.planDisplayLabel(plan) || '')
          .trim()
          .toLowerCase();
        return Boolean(
          (planName && value.includes(planName)) ||
          (displayLabel && value.includes(displayLabel))
        );
      }
    }
  };

  A.controlLabelText = (control) => {
    if (!(control instanceof Element)) return '';

    const root = A.state?.root;
    const id = String(control.id || '');
    if (id && root) {
      try {
        const label = root.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (label) return String(label.textContent || '');
      } catch (_) {}
    }

    return String(control.closest('label')?.textContent || '');
  };

  A.controlMatchesPlan = (control, planId) => {
    if (!(control instanceof Element)) return false;

    const desiredId = A.normaliseId(planId);
    const signals = [
      control.getAttribute('value'),
      control.id,
      ...Object.values(control.dataset || {})
    ];

    if (
      signals.some((signal) => {
        const raw = String(signal || '');
        return A.normaliseId(raw) === desiredId || raw.includes(desiredId);
      })
    ) {
      return true;
    }

    return A.planTextMatches(A.controlLabelText(control), desiredId);
  };

  A.optionMatchesPlan = (option, planId) => {
    if (!(option instanceof HTMLOptionElement)) return false;

    const desiredId = A.normaliseId(planId);
    const signals = [
      option.value,
      option.id,
      ...Object.values(option.dataset || {})
    ];

    if (
      signals.some((signal) => {
        const raw = String(signal || '');
        return A.normaliseId(raw) === desiredId || raw.includes(desiredId);
      })
    ) {
      return true;
    }

    return A.planTextMatches(option.textContent, desiredId);
  };

  A.activateNativeControl = (control) => {
    if (!(control instanceof HTMLElement)) return;

    if ('disabled' in control) control.disabled = false;
    control.removeAttribute('disabled');

    try {
      control.click();
    } catch (_) {
      if (control instanceof HTMLInputElement) control.checked = true;
      control.dispatchEvent(new Event('input', { bubbles: true }));
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  A.syncAppstleCadenceControls = (planId) => {
    const root = A.state?.root;
    const desiredId = A.normaliseId(planId || '');
    if (!root?.isConnected || !desiredId) return;

    Array.from(root.querySelectorAll('select'))
      .filter(A.isAppstleControl)
      .forEach((select) => {
        const option = Array.from(select.options).find((candidate) =>
          A.optionMatchesPlan(candidate, desiredId)
        );
        if (!option) return;

        const changed = select.value !== option.value;
        select.disabled = false;
        select.removeAttribute('disabled');
        select.value = option.value;
        option.selected = true;
        select.dataset.femmeAppstleNativePlanState = 'subscription';

        if (changed) {
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

    Array.from(root.querySelectorAll('input[type="radio"]'))
      .filter((radio) => {
        if (!A.isAppstleControl(radio)) return false;
        if (radio.name === 'selling_plan') return false;
        return A.controlMatchesPlan(radio, desiredId);
      })
      .forEach((radio) => {
        radio.dataset.femmeAppstleNativePlanState = 'subscription';
        if (!radio.checked) A.activateNativeControl(radio);
      });
  };

  A.syncAppstleWidgetSelection = (planId) => {
    const root = A.state?.root;
    if (!root?.isConnected) return;

    const desiredId = A.normaliseId(planId || '');
    const radios = Array.from(
      root.querySelectorAll(
        'input[type="radio"][name="selling_plan"]:not([data-femme-appstle-plan="true"])'
      )
    ).filter(A.isAppstleSellingPlanRadio);

    if (!radios.length) {
      if (desiredId) A.syncAppstleCadenceControls(desiredId);
      return;
    }

    radios.forEach((radio) => {
      radio.disabled = false;
      radio.removeAttribute('disabled');
      radio.dataset.femmeAppstleNativePlanState = desiredId
        ? 'subscription'
        : 'one_time';
    });

    if (!desiredId) {
      const oneTimeTargets = radios.filter(
        (radio) => !A.normaliseId(radio.value || '')
      );

      oneTimeTargets.forEach((target) => {
        if (!target.checked) A.activateNativeControl(target);
      });
      return;
    }

    const exactTargets = radios.filter((radio) =>
      A.controlMatchesPlan(radio, desiredId)
    );

    if (exactTargets.length) {
      exactTargets.forEach((target) => {
        if (!target.checked) A.activateNativeControl(target);
      });
    } else {
      const subscriptionTarget =
        radios.find(
          (radio) =>
            radio.checked && Boolean(A.normaliseId(radio.value || ''))
        ) ||
        radios.find((radio) => Boolean(A.normaliseId(radio.value || '')));

      if (subscriptionTarget && !subscriptionTarget.checked) {
        A.activateNativeControl(subscriptionTarget);
      }
    }

    A.syncAppstleCadenceControls(desiredId);
  };

  A.syncNativePlanInputs = () => {
    const root = A.state?.root;
    if (!root?.isConnected) return;

    const isSubscription = A.state.selectionType === 'subscription';
    const planId = isSubscription
      ? A.normaliseId(A.state.selectedPlanId || '')
      : '';

    A.syncAppstleWidgetSelection(planId);

    root.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form
        .querySelectorAll(
          '[name="selling_plan"]:not([data-femme-appstle-plan="true"])'
        )
        .forEach((control) => {
          const stateName = planId ? 'subscription' : 'one_time';
          control.dataset.femmeAppstleNativePlanState = stateName;

          if (A.isAppstleSellingPlanRadio(control)) {
            control.disabled = false;
            control.removeAttribute('disabled');
            return;
          }

          if (
            control instanceof HTMLInputElement &&
            String(control.type || '').toLowerCase() === 'radio'
          ) {
            control.disabled = true;
            control.setAttribute('disabled', 'disabled');
            return;
          }

          if (control instanceof HTMLSelectElement) {
            const desiredValue = planId || '';
            const option = Array.from(control.options).find((candidate) =>
              desiredValue
                ? A.optionMatchesPlan(candidate, desiredValue)
                : !A.normaliseId(candidate.value || '')
            );

            if (option) {
              control.disabled = false;
              control.removeAttribute('disabled');
              control.value = option.value;
              option.selected = true;
            } else {
              control.disabled = true;
              control.setAttribute('disabled', 'disabled');
            }
            return;
          }

          control.disabled = false;
          control.removeAttribute('disabled');
          if ('value' in control) control.value = planId;
          control.setAttribute('value', planId);
        });
    });
  };

  A.scheduleNativePlanSync = () => {
    [0, 60, 180].forEach((delay) => {
      window.setTimeout(A.syncNativePlanInputs, delay);
    });
  };

  document.addEventListener(
    'fsc:purchase-option-changed',
    A.scheduleNativePlanSync
  );

  window.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !A.state?.root) return;

      const purchaseControl = target.closest(
        'button.product-form__submit[name="add"],button[name="add"],input[name="add"],form[action*="/cart/add"] [type="submit"]'
      );

      if (!purchaseControl || !A.state.root.contains(purchaseControl)) return;
      A.syncNativePlanInputs();
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (
        !(form instanceof HTMLFormElement) ||
        !A.state?.root?.contains(form) ||
        !String(form.action || '').includes('/cart/add')
      ) {
        return;
      }

      A.syncNativePlanInputs();
    },
    true
  );

  A.parseJson = (el) => {
    try {
      return el ? JSON.parse(el.textContent || '{}') : null;
    } catch (error) {
      console.error('[femme Appstle] Invalid JSON.', error);
      return null;
    }
  };

  A.money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  A.normaliseId = (value) =>
    String(value || '').replace(/^gid:\/\/shopify\/[^/]+\//, '');

  A.planDisplayLabel = (plan) => {
    const id = A.normaliseId(plan?.id);
    if (A.PLAN_LABELS[id]) return A.PLAN_LABELS[id];

    const name = String(plan?.name || '').trim();
    if (/fortnight/i.test(name)) return 'Every 2 weeks';

    const weekMatch = name.match(/^(?:every\s+)?(\d+)\s*weeks?/i);
    if (weekMatch) {
      const weeks = Number(weekMatch[1]);
      return weeks === 1 ? 'Every week' : `Every ${weeks} weeks`;
    }

    const monthMatch = name.match(/^(?:every\s+)?(\d+)\s*monthly/i)
      || name.match(/^(?:every\s+)?(\d+)\s*months?/i);
    if (monthMatch) {
      const months = Number(monthMatch[1]);
      return months === 1 ? 'Every month' : `Every ${months} months`;
    }

    if (/^monthly(?:\s+subscription)?$/i.test(name)) return 'Every month';

    return name || 'Subscription';
  };

  A.deliveryBenefit = (plan) => {
    const label = A.planDisplayLabel(plan);
    if (label === 'Every month') return 'Delivers monthly';
    if (/^Every\s+/i.test(label)) return `Delivers ${label.toLowerCase()}`;
    return `Delivery: ${label}`;
  };

  A.defaultPlan = (plans = []) => {
    if (!Array.isArray(plans) || !plans.length) return null;
    return (
      plans.find((plan) => A.normaliseId(plan.id) === A.DEFAULT_PLAN_ID) ||
      plans.find((plan) => /(^|\s)1\s*Monthly/i.test(String(plan.name || ''))) ||
      plans.find((plan) => /^Monthly/i.test(String(plan.name || ''))) ||
      plans[0]
    );
  };

  A.productUrl = (value) => {
    const raw = String(value || window.location.pathname);
    const source = raw.startsWith('/') ? raw : `/products/${raw}`;
    const url = new URL(source, window.location.origin);
    url.hash = '';
    url.search = '';
    let path = url.pathname.replace(/\/$/, '');
    if (!path.endsWith('.js')) path += '.js';
    return `${url.origin}${path}`;
  };

  A.fetchProduct = async (value) => {
    const url = A.productUrl(value);
    if (A.cache.has(url)) return A.cache.get(url);

    const request = fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product JSON ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        A.cache.delete(url);
        throw error;
      });

    A.cache.set(url, request);
    return request;
  };

  A.appstleGroups = (product) => {
    const allGroups = Array.isArray(product?.selling_plan_groups)
      ? product.selling_plan_groups
      : [];

    return allGroups.filter((group) => {
      const appId = String(group.app_id || group.appId || '').toLowerCase();
      return appId.includes(A.APP_ID);
    });
  };

  A.nativeAppstlePlanIds = (groups = []) => {
    const planIds = new Set();
    groups.forEach((group) => {
      (group.selling_plans || group.sellingPlans || []).forEach((plan) => {
        const id = A.normaliseId(plan?.id);
        if (id) planIds.add(id);
      });
    });

    if (!planIds.size) return new Set();

    const nativeMarkup = Array.from(document.querySelectorAll(A.APPSTLE_SELECTOR))
      .map((node) => node.outerHTML || node.textContent || '')
      .join(' ');

    if (!nativeMarkup) return new Set();

    return new Set(
      Array.from(planIds).filter((planId) => nativeMarkup.includes(planId))
    );
  };

  A.resolveAppstleGroups = (product) => {
    const groups = A.appstleGroups(product);
    if (groups.length <= 1) return groups;

    const nativePlanIds = A.nativeAppstlePlanIds(groups);
    const matchingGroups = groups.filter((group) =>
      (group.selling_plans || group.sellingPlans || []).some((plan) =>
        nativePlanIds.has(A.normaliseId(plan?.id))
      )
    );

    if (matchingGroups.length === 1) return matchingGroups;

    if (matchingGroups.length > 1) {
      const selectedSignals = Array.from(
        document.querySelectorAll(A.APPSTLE_SELECTOR)
      )
        .flatMap((nativeRoot) =>
          Array.from(nativeRoot.querySelectorAll('input:checked, option:checked'))
        )
        .map((control) => String(control.value || control.textContent || ''))
        .join(' ');

      const selectedGroup = matchingGroups.find((group) =>
        (group.selling_plans || group.sellingPlans || []).some((plan) => {
          const planId = A.normaliseId(plan?.id);
          return planId && selectedSignals.includes(planId);
        })
      );

      if (selectedGroup) return [selectedGroup];
    }

    return [];
  };

  A.hasAmbiguousAppstleGroups = (product) =>
    A.appstleGroups(product).length > 1 &&
    A.resolveAppstleGroups(product).length !== 1;

  A.normaliseAdjustment = (adjustment) => {
    if (!adjustment || typeof adjustment !== 'object') return null;

    const valueType = String(
      adjustment.value_type ||
      adjustment.valueType ||
      adjustment.adjustment_type ||
      adjustment.adjustmentType ||
      ''
    ).toLowerCase();

    let value = adjustment.value;
    if (value == null && adjustment.adjustment_value != null) {
      value = adjustment.adjustment_value;
    }
    if (value == null && adjustment.adjustmentValue != null) {
      value = adjustment.adjustmentValue;
    }
    if (value && typeof value === 'object') {
      value =
        value.percentage ??
        value.adjustmentPercentage ??
        value.amount ??
        value.value ??
        null;
    }

    const orderCountRaw =
      adjustment.order_count ??
      adjustment.orderCount ??
      adjustment.after_cycle ??
      adjustment.afterCycle ??
      null;

    const orderCount =
      orderCountRaw == null || orderCountRaw === ''
        ? null
        : Number(orderCountRaw);

    return {
      position: Number(adjustment.position || 0) || 0,
      orderCount: Number.isFinite(orderCount) ? orderCount : null,
      valueType,
      value: Number(value)
    };
  };

  A.planAdjustments = (plan) => {
    const raw =
      plan?.price_adjustments ||
      plan?.priceAdjustments ||
      plan?.pricing_policies ||
      plan?.pricingPolicies ||
      [];

    return (Array.isArray(raw) ? raw : [])
      .map(A.normaliseAdjustment)
      .filter(Boolean);
  };

  A.allocationAdjustmentPrices = (allocation) => {
    const raw =
      allocation?.price_adjustments ||
      allocation?.priceAdjustments ||
      [];

    return (Array.isArray(raw) ? raw : [])
      .map((adjustment) => Number(adjustment?.price ?? adjustment))
      .filter((price) => Number.isFinite(price) && price >= 0);
  };

  A.discountPercent = (compareAt, price) => {
    const compare = Number(compareAt || 0);
    const current = Number(price || 0);
    return compare > current && compare > 0
      ? Math.round(((compare - current) / compare) * 100)
      : 0;
  };

  A.planTerms = (plan, variant) => {
    const initialSaving = A.discountPercent(
      plan?.compareAt || variant?.price || 0,
      plan?.price || 0
    );

    const adjustments = Array.isArray(plan?.adjustments)
      ? plan.adjustments
      : [];
    const adjustmentPrices = Array.isArray(plan?.adjustmentPrices)
      ? plan.adjustmentPrices
      : [];

    const first = adjustments[0] || null;
    const later = adjustments[1] || null;

    let laterSaving = 0;
    if (later) {
      if (later.valueType.includes('percentage') && Number.isFinite(later.value)) {
        laterSaving = Math.round(later.value);
      } else if (adjustmentPrices[1] != null) {
        laterSaving = A.discountPercent(
          plan?.compareAt || variant?.price || 0,
          adjustmentPrices[1]
        );
      }
    }

    const initialOrderCount =
      adjustments.length > 1 && first?.orderCount != null
        ? Number(first.orderCount)
        : null;

    return {
      initialSaving,
      laterSaving,
      initialOrderCount,
      hasLaterChange:
        adjustments.length > 1 &&
        laterSaving > 0 &&
        laterSaving !== initialSaving
    };
  };

  A.ordinal = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value || '');
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  A.applyPlanPresentation = (plan, variant) => {
    const S = A.state;
    if (!S?.config || !plan || !variant) return;

    const base = S.baseConfig || S.config;
    const terms = A.planTerms(plan, variant);
    const isLegacyGroup =
      A.normaliseId(plan.groupId) === A.LEGACY_GROUP_ID;

    S.config.subscriptionLabel =
      terms.initialSaving > 0
        ? terms.hasLaterChange
          ? 'Subscribe for [saving]% off first order'
          : 'Subscribe & save [saving]%'
        : String(base.subscriptionLabel || 'Subscribe & save');

    S.config.appendSavingLabel = false;

    S.config.showSavingBenefit =
      Boolean(base.showSavingBenefit) && isLegacyGroup;

    S.config.savingBenefit = String(base.savingBenefit || '');

    const neutralBenefits = (Array.isArray(base.benefits) ? base.benefits : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .filter((value) => !/\b\d+(?:\.\d+)?%\s+off\b/i.test(value));

    if (
      terms.hasLaterChange &&
      terms.initialOrderCount != null &&
      terms.initialOrderCount >= 1
    ) {
      neutralBenefits.splice(
        Math.min(1, neutralBenefits.length),
        0,
        `${terms.laterSaving}% off from your ${A.ordinal(
          terms.initialOrderCount + 1
        )} order`
      );
    }

    S.config.benefits = neutralBenefits;
  };

  A.appstleData = (product) => {
    const groups = A.resolveAppstleGroups(product);

    const plans = new Map();
    groups.forEach((group) => {
      (group.selling_plans || group.sellingPlans || []).forEach((plan) => {
        const id = A.normaliseId(plan.id);
        plans.set(id, {
          id,
          name: String(plan.name || ''),
          groupId: A.normaliseId(group.id),
          groupName: String(group.name || ''),
          appId: String(group.app_id || group.appId || ''),
          displayLabel: A.PLAN_LABELS[id] || '',
          adjustments: A.planAdjustments(plan)
        });
      });
    });

    const variants = new Map();
    (product?.variants || []).forEach((variant) => {
      const allocations =
        variant.selling_plan_allocations ||
        variant.sellingPlanAllocations ||
        [];

      const variantPlans = (Array.isArray(allocations) ? allocations : [])
        .map((allocation) => {
          const allocationPlanId = A.normaliseId(
            allocation.selling_plan_id ||
            allocation.sellingPlanId ||
            allocation.selling_plan?.id ||
            allocation.sellingPlan?.id
          );
          const plan = plans.get(allocationPlanId);
          return plan
            ? {
                ...plan,
                price: Number(allocation.price || 0),
                compareAt: Number(
                  allocation.compare_at_price ||
                  allocation.compareAtPrice ||
                  variant.price ||
                  0
                ),
                adjustmentPrices: A.allocationAdjustmentPrices(allocation)
              }
            : null;
        })
        .filter(Boolean);

      variants.set(String(variant.id), {
        id: String(variant.id),
        price: Number(variant.price || 0),
        plans: variantPlans
      });
    });

    return variants;
  };
})();
